import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { Phone, MessageCircle, Sparkles, Check, ShieldCheck, Clock, Heart, Star, Mail, Quote, Bell, Gift } from "lucide-react";
import { Navbar, Footer, StatusBadge } from "@/components/Layout";
import { Seo, SeoText } from "@/components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, useAuth, formatApiError } from "@/context/AuthContext";
import { spaceFor } from "@/components/AuthForms";
import { ZODIAC_IMAGES } from "@/data/zodiac";

const HERO_BG = "/images/hero-bg.webp"; // auto-hébergée (WebP), plus d'appel externe

const HERO_LINES = ["Voyance en ligne :", "voyants & médiums", "par téléphone ou chat."];

const CHAPTERS = [
  { n: "01", t: "Je crée mon compte", d: "Inscription rapide et gratuite : 5 minutes offertes sur votre 1ère consultation, sans carte bancaire. Vos informations restent strictement confidentielles à chaque étape." },
  { n: "02", t: "Je choisis un forfait", d: "Un forfait clair et sans surprise. Vous maîtrisez votre budget à l'avance, sans dépassement." },
  { n: "03", t: "Je sélectionne mon médium", d: "Parcourez les fiches de nos médiums et choisissez celle qui résonne avec vous." },
  { n: "04", t: "Je démarre ma consultation", d: "Par téléphone ou par chat, à la minute. Un minuteur s'arrête pile à la fin de votre forfait." },
];

const VALUES = [
  { icon: Sparkles, t: "Authenticité", d: "Des dons véritables, une écoute sincère." },
  { icon: ShieldCheck, t: "Confidentialité", d: "Vos échanges restent strictement privés." },
  { icon: Heart, t: "Bienveillance", d: "Un espace d'écoute, sans jugement." },
  { icon: Clock, t: "Sans attente", d: "Disponibles quand vous en avez besoin." },
];

const FAQ = [
  { q: "Comment fonctionne la consultation à la minute ?", a: "Vous achetez un forfait (par exemple 15 minutes). Un minuteur démarre au début de votre consultation par téléphone ou par chat, et s'arrête automatiquement lorsque votre temps est écoulé. Aucun dépassement possible." },
  { q: "Puis-je répartir mon forfait sur plusieurs consultations ?", a: "Oui. Votre crédit reste disponible dans votre espace client et peut être utilisé en plusieurs fois, avec le médium de votre choix." },
  { q: "Quels sont les tarifs ?", a: "Nos forfaits démarrent à 30€, avec du crédit offert sur les forfaits 50€ et 100€. Le tarif démarre à {rate} € la minute, selon la médium choisie." },
  { q: "Les consultations sont-elles confidentielles ?", a: "Absolument. Tous vos échanges sont strictement privés et aucune donnée personnelle n'est divulguée." },
  { q: "Comment se déroule le paiement ?", a: "Le paiement s'effectue en ligne par carte bancaire via un système 100% sécurisé (3D Secure)." },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}>{children}</motion.div>
);

const Stars = ({ n = 5, cls = "text-goldsoft" }) => (
  <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(n) ? cls + " fill-current" : "text-[var(--border)]"}`} />)}</div>
);

export default function Landing() {
  const [packages, setPackages] = useState([]);
  const [rate, setRate] = useState(2.5);
  const [mediums, setMediums] = useState([]);
  const [modeFilter, setModeFilter] = useState("all"); // all | both | phone | chat — filtres façon Kang, TOUTES par défaut
  const [reviews, setReviews] = useState([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [horoSigns, setHoroSigns] = useState([]);
  const [anyAvailable, setAnyAvailable] = useState(false);
  const [autoPromo, setAutoPromo] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const ctaTarget = user ? spaceFor(user) : "/inscription";
  const isClient = user && user.role !== "medium" && user.role !== "admin";
  const [callMedium, setCallMedium] = useState(null);
  const [callPhone, setCallPhone] = useState("");
  const [callBusy, setCallBusy] = useState(false);
  const [emailMedium, setEmailMedium] = useState(null);
  const [emailMsg, setEmailMsg] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const startChat = async (slug) => {
    if (!isClient) { navigate(ctaTarget); return; }
    try {
      const { data } = await axios.post(`${API}/consultations/start`, { medium: slug, mode: "chat" });
      navigate(`/consultation/${data.consultation_id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402) { toast.error("Crédit insuffisant. Choisissez un forfait."); navigate("/espace"); return; }
      if (err.response?.status === 409 && d?.code) {
        if (d.code === "BUSY") { toast.info("Médium occupé — rejoignez la file depuis votre espace."); navigate("/espace"); return; }
        toast.error(d.message || "Indisponible"); return;
      }
      toast.error(formatApiError(d));
    }
  };
  const openCall = (slug) => { if (!isClient) { navigate(ctaTarget); return; } setCallMedium(slug); setCallPhone(""); };
  const submitCall = async () => {
    setCallBusy(true);
    try {
      const { data } = await axios.post(`${API}/consultations/start`, { medium: callMedium, mode: "phone" });
      await axios.post(`${API}/callbacks`, { medium: callMedium, phone: callPhone });
      toast.success("Demande enregistrée, vous allez être rappelé(e).");
      navigate(`/consultation/${data.consultation_id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402) { toast.error("Crédit insuffisant. Choisissez un forfait."); navigate("/espace"); return; }
      toast.error(formatApiError(d));
    } finally { setCallBusy(false); }
  };
  const openEmail = (m) => { if (!isClient) { navigate(ctaTarget); return; } setEmailMedium(m); setEmailMsg(""); };
  const submitEmail = async () => {
    if (emailMsg.trim().length < 10) { toast.error("Merci d'écrire votre message (10 caractères minimum)."); return; }
    setEmailBusy(true);
    try {
      const { data } = await axios.post(`${API}/email-consultations/checkout`, { medium: emailMedium.slug, message: emailMsg, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setEmailBusy(false); }
  };
  const heroRef = useRef(null);
  const reviewTrackRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1).replace(".", ",") : "5,0";
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.3]);

  const ReviewCard = ({ r, i }) => {
    const med = mediums.find((m) => m.slug === r.medium);
    return (
      <div data-review-card data-testid={`review-${i}`} className="w-full sm:w-[360px] lg:w-full bg-white rounded-2xl p-7 flex flex-col shadow-[0_24px_60px_-24px_rgba(0,0,0,0.55)] h-full">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="font-serif text-xl text-[var(--text)] leading-tight">{r.author}</p>
            <div className="mt-1.5"><Stars n={r.rating} /></div>
          </div>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--gold-soft)] to-[var(--violet)] text-white flex items-center justify-center font-serif text-xl shrink-0">{(r.author || "?")[0].toUpperCase()}</div>
        </div>
        <div className="flex gap-3 flex-1">
          <Quote className="w-7 h-7 text-[var(--gold-soft)] shrink-0 fill-current opacity-90" />
          <p className="text-[var(--muted)] leading-relaxed text-sm">{r.comment}</p>
        </div>
        <div className="mt-5 pt-4 border-t border-[var(--border)] flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] text-green-700 bg-green-500/10 border border-green-500/20 rounded-full px-2.5 py-1"><Check className="w-3 h-3" /> {r.kind === "temoignage" ? "Témoignage client" : "Avis vérifié"}</span>
          <div className="flex items-center gap-2 shrink-0">
            {med?.image && <img src={med.image} alt={med.name} className="w-7 h-7 rounded-full object-cover border border-[var(--border)]" />}
            <span className="text-[var(--muted)] text-xs whitespace-nowrap">Avec <span className="text-[var(--text)] font-medium capitalize">{med?.name || r.medium}</span></span>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    axios.get(`${API}/packages`).then(({ data }) => { setPackages(data.packages); setRate(data.per_minute_rate); });
    // Les médiums DISPONIBLES passent en premier (puis En consultation, puis Hors ligne).
    // Tri stable : à statut égal, chacune garde sa place d'origine (ex. toutes connectées = ordre inchangé).
    const sortByAvailability = (list) => {
      const rank = { available: 0, busy: 1, offline: 2 };
      return [...list].sort((a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3));
    };
    axios.get(`${API}/mediums`).then(({ data }) => { setMediums(sortByAvailability(data.mediums)); setAnyAvailable(data.any_available); });
    axios.get(`${API}/promotions/active`).then(({ data }) => setAutoPromo(data.promo)).catch(() => {});
    axios.get(`${API}/reviews?limit=40`).then(({ data }) => { setReviews(data.reviews); setReviewsTotal(data.total ?? data.reviews.length); }).catch(() => {});
    axios.get(`${API}/horoscope/signs`).then(({ data }) => setHoroSigns(data.signs || [])).catch(() => {});
    // Robots/prérendu (webdriver) : pas de polling — le HTML doit se stabiliser vite pour Google.
    if (navigator.webdriver) return;
    // Statuts quasi instantanés (façon Kang) : la page se met à jour toutes les 2 s,
    // sans que le visiteur ait à actualiser — une médium qui se (dé)connecte apparaît/disparaît aussitôt.
    const t = setInterval(() => axios.get(`${API}/mediums`).then(({ data }) => { setMediums(sortByAvailability(data.mediums)); setAnyAvailable(data.any_available); }).catch(() => {}), 2000);
    return () => clearInterval(t);
  }, []);

  // Défilement auto des avis (mobile/tablette) : UNE SEULE liste dans le DOM,
  // la boucle est gérée en JS (défilement puis retour au début), sans dupliquer le contenu.
  useEffect(() => {
    const el = reviewTrackRef.current;
    if (!el || !reviews.length || navigator.webdriver) return;
    let paused = false, last = 0, raf = 0;
    const step = (t) => {
      if (!last) last = t;
      const dt = Math.min(t - last, 50); last = t;
      if (!paused) {
        el.scrollLeft += dt * 0.03;
        if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 2) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    const pause = () => { paused = true; };
    const resume = () => { paused = false; last = 0; };
    el.addEventListener("pointerenter", pause);
    el.addEventListener("pointerdown", pause);
    el.addEventListener("pointerleave", resume);
    el.addEventListener("pointerup", resume);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("pointerenter", pause);
      el.removeEventListener("pointerdown", pause);
      el.removeEventListener("pointerleave", resume);
      el.removeEventListener("pointerup", resume);
    };
  }, [reviews]);

  return (
    <div className="relative">
      <Seo
        title="Voyance en ligne | Médium – Voyance Étoilée"
        description="Voyance en ligne : 5 min offertes avec une médium, par téléphone ou chat 7j/7. Tarot et oracle gratuits, horoscope et numérologie."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ.map((f) => ({ "@type": "Question", "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a.replace("{rate}", rate.toFixed(2).replace(".", ",")) } })),
        }}
      />
      <Navbar overHero />

      {/* HERO — night */}
      <section ref={heroRef} className="night relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
          <motion.img src={HERO_BG} alt="" width={1920} height={1221} fetchPriority="high" className="w-full h-full object-cover"
            initial={{ scale: 1.06 }} animate={{ scale: 1.16 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
            style={{ opacity: 0.92 }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #171334 6%, rgba(23,19,52,0.20) 45%, rgba(23,19,52,0.28) 100%)" }} />
          <motion.div className="absolute -top-32 -right-24 w-[36rem] h-[36rem] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(212,175,55,0.28), transparent 65%)" }}
            animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.12, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-24 pt-40 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex flex-wrap items-center gap-4 mb-8">
            <span className="font-sans text-sm md:text-base tracking-[0.22em] font-semibold text-goldsoft gold-shimmer-anim">VOYANCE ÉTOILÉE · MÉDIUMNITÉ · TÉLÉPATHIE</span>
            {anyAvailable && (
              <span data-testid="home-online-badge" className="inline-flex items-center gap-2.5 text-sm md:text-base font-sans font-semibold text-green-200 bg-green-500/20 border border-green-400/40 rounded-full px-4 py-1.5 shadow-[0_0_18px_rgba(74,222,128,0.25)]">
                <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" /> Un médium est disponible maintenant
              </span>
            )}
          </motion.div>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight max-w-5xl text-[#f5f3ff]">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}>
                  {i === 2 ? <em className="gold-shimmer gold-shimmer-anim not-italic">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 max-w-2xl">
            <p className="text-[#e9e4ff]/90 text-base md:text-lg leading-relaxed">
              <span className="font-serif italic text-2xl md:text-3xl text-goldsoft gold-shimmer-anim">Votre chemin mérite de la clarté.</span><br />
              Consultations sincères avec nos médiums, par téléphone ou par chat, à la minute.
            </p>
            <Link to={ctaTarget} data-testid="hero-cta" className="btn-gold px-8 py-4 rounded-full text-sm font-medium whitespace-nowrap self-start">{user ? "Accéder à mon espace" : "Commencer ma consultation"}</Link>
          </motion.div>

          {!user && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.7 }} className="mt-6">
              <Link to="/inscription" data-testid="hero-free-offer"
                className="group inline-flex items-center gap-3 rounded-full pl-3 pr-5 py-2.5 bg-goldsoft/15 border border-goldsoft/40 hover:bg-goldsoft/25 transition-colors">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-goldsoft/25 border border-goldsoft/40">
                  <Gift className="w-4 h-4 text-goldsoft" strokeWidth={1.6} />
                </span>
                <span className="text-sm text-[#f5f3ff]">
                  <span className="font-medium text-goldsoft">5 minutes offertes</span> sur votre 1ère consultation, sans carte bancaire
                </span>
                <span className="text-goldsoft opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all">→</span>
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* AUTO PROMO BANNER */}
      {autoPromo && (
        <div data-testid="home-promo-banner" className="bg-goldsoft text-[#171334] py-3 text-center text-sm font-medium flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" /> {autoPromo.label} : +{autoPromo.bonus_percent}% de crédit offert sur tous les forfaits !
        </div>
      )}

      {/* MARQUEE */}
      <div className="border-y border-[var(--border)] py-6 bg-[var(--surface-2)]">
        <Marquee speed={30} gradient={false}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="font-serif text-3xl md:text-4xl text-[var(--muted)] mx-16 flex items-center gap-16">
              Voyance pure <Sparkles className="w-5 h-5 text-goldsoft" /> Clarté <Sparkles className="w-5 h-5 text-goldsoft" /> Sérénité <Sparkles className="w-5 h-5 text-goldsoft" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* MEDIUMS */}
      <section id="mediums" className="max-w-7xl mx-auto px-6 md:px-10 py-28 scroll-mt-24">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] gold-shimmer mb-4">NOS MÉDIUMS</p>
          <h2 className="font-serif text-4xl md:text-6xl max-w-3xl mb-10 md:mb-20 leading-[1.06]">Des médiums à votre écoute,<br /><em className="gold-shimmer">en toute bienveillance.</em></h2>
        </Reveal>
        {/* Filtres par mode de consultation — façon Kang */}
        <Reveal>
          <div className="flex flex-wrap justify-center gap-2.5 mb-10 md:mb-14" data-testid="medium-filters">
            {[
              { key: "all", label: "Toutes" },
              { key: "phone", label: "Appel" },
              { key: "chat", label: "Chat" },
            ].map((f) => (
              <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setModeFilter(f.key)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm ${
                  modeFilter === f.key
                    ? "bg-[#171334] text-white shadow-md"
                    : "bg-white border border-[var(--border)] text-[var(--muted)] hover:border-[var(--violet)] hover:text-[var(--text)]"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 max-w-5xl mx-auto">
          {mediums.filter((m) => {
            if (modeFilter !== "chat") return true;  // "Toutes" et "Appel" : toutes les médiums consultent par téléphone
            return m.accepts_chat !== false;         // "Chat" : médiums joignables par chat
          }).map((m, i) => (
            <Reveal key={m.slug} delay={i * 0.15}>
              <div className="group w-full max-w-[340px] mx-auto" data-testid={`home-medium-card-${m.slug}`}>
                {/* Badges appels + avis au-dessus de la photo (comme avant) */}
                <div className="flex items-center gap-2.5 mb-3 flex-wrap" data-testid={`home-stats-${m.slug}`}>
                  <span data-testid={`home-consultations-${m.slug}`} className="inline-flex items-center gap-1.5 bg-goldsoft text-[#201a08] rounded-full px-4 py-2 font-bold text-sm shadow-md">
                    <Phone className="w-4 h-4" /> {(m.consultations_count || 0).toLocaleString("fr-FR")} appels reçus
                  </span>
                  <span data-testid={`home-reviews-${m.slug}`} className="inline-flex items-center gap-1.5 border-2 border-gold text-[var(--text)] rounded-full px-4 py-2 font-semibold text-sm">
                    <Star className="w-4 h-4 fill-current text-gold" /> {(m.reviews_count || 0).toLocaleString("fr-FR")} avis
                  </span>
                </div>
                {/* Photo + badge statut façon Kang : toujours visible, coloré */}
                <div onClick={() => navigate(`/voyance/${m.slug}`)} data-testid={`home-medium-photo-${m.slug}`} className="relative overflow-hidden rounded-[1.75rem] aspect-[4/5] w-full mb-4 cursor-pointer shadow-lg">
                  {m.image ? (
                    <img src={m.image} alt={m.name} width="600" height="800" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
                  ) : (
                    <div className="w-full h-full bg-[#171334] flex items-center justify-center"><span className="font-serif text-8xl text-goldsoft/70">{(m.name || "M")[0]}</span></div>
                  )}
                  {/* Badge statut sur la photo — lisible d'un coup d'œil */}
                  <span data-testid={`home-status-${m.slug}`} className={`absolute top-3 left-3 flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold shadow-md ${
                    m.status === "available" ? "bg-green-500 text-white" :
                    m.status === "busy" ? "bg-amber-500 text-white" : "bg-gray-500/90 text-white"}`}>
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    {m.status === "available" ? "Disponible" : m.status === "busy" ? "En consultation" : "Hors ligne"}
                  </span>
                  <span className="absolute top-3 right-3 text-[10px] font-mono tracking-wider text-[#201a08] bg-goldsoft/90 rounded-full px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">PROFIL →</span>
                  {/* Badge « Nouveau » : médium arrivée sur la plateforme depuis moins de 30 jours */}
                  {m.is_new && (
                    <span data-testid={`home-new-${m.slug}`} className="absolute top-14 left-3 flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold shadow-md bg-gold text-[#201a08]">
                      <Sparkles className="w-3.5 h-3.5" /> Nouveau
                    </span>
                  )}
                  {/* Nom en bas de la photo, façon Kang */}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pt-10 pb-3.5 pointer-events-none">
                    <p className="font-serif text-3xl text-white leading-none">{m.name}</p>
                  </div>
                </div>
                {/* Infos en bas comme avant : expérience, rôle, note, prix */}
                <div className="px-1 mb-3" data-testid={`home-medium-card-info-${m.slug}`}>
                  <p className="font-mono text-[11px] tracking-[0.2em] text-gold font-semibold">{m.years} ANS D'EXPÉRIENCE</p>
                  <p className="text-[var(--muted)] text-sm font-medium">{m.role}</p>
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <div className="flex items-center gap-1.5">
                      <Stars n={m.rating} /><span className="text-xs text-gold font-mono font-bold">{m.rating?.toFixed(1)}</span>
                    </div>
                    <span className="text-gold font-bold text-sm">{Number(m.rate_per_min).toFixed(2).replace(".", ",")} €/min</span>
                  </div>
                </div>
                {/* Boutons d'action façon Kang : grands, colorés, pleine largeur — jamais décalés sur mobile */}
                {m.status === "offline" ? (
                  <div className="grid grid-cols-1 gap-2">
                    <button onClick={() => navigate(`/voyance/${m.slug}`)} data-testid={`home-alert-${m.slug}`} className="btn-gold w-full py-3 rounded-2xl text-sm font-bold flex items-center justify-center gap-2"><Bell className="w-4 h-4" /> M'alerter de son retour</button>
                    {m.email_enabled && <button onClick={() => openEmail(m)} data-testid={`home-email-${m.slug}`} className="w-full py-3 rounded-2xl text-sm font-bold border-2 border-[var(--violet)] text-[var(--violet)] hover:bg-[var(--violet)] hover:text-white transition-colors flex items-center justify-center gap-2"><Mail className="w-4 h-4" /> Email · {(m.email_rate_cents / 100).toFixed(2).replace(".", ",")} €</button>}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {m.accepts_chat !== false && (
                      <button onClick={() => startChat(m.slug)} data-testid={`home-chat-${m.slug}`} className="bg-[var(--violet)] hover:opacity-90 transition-opacity text-white w-full py-3 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 shadow-md"><MessageCircle className="w-4 h-4" /> {m.status === "busy" ? "File d'attente" : "Chat"}</button>
                    )}
                    <button onClick={() => openCall(m.slug)} data-testid={`home-phone-${m.slug}`} className={`btn-gold w-full py-3 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 shadow-md ${m.accepts_chat === false ? "col-span-2" : ""}`}><Phone className="w-4 h-4" /> Appel</button>
                    {m.email_enabled && <button onClick={() => openEmail(m)} data-testid={`home-email-${m.slug}`} className="col-span-2 w-full py-3 rounded-2xl text-sm font-bold border-2 border-[var(--violet)] text-[var(--violet)] hover:bg-[var(--violet)] hover:text-white transition-colors flex items-center justify-center gap-2"><Mail className="w-4 h-4" /> Email · {(m.email_rate_cents / 100).toFixed(2).replace(".", ",")} €</button>}
                  </div>
                )}
                <button onClick={() => navigate(`/voyance/${m.slug}`)} data-testid={`home-medium-info-${m.slug}`} className="text-sm text-gold hover:text-goldsoft font-medium inline-flex items-center gap-1 mt-3 transition-colors">À propos de {m.name} →</button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOROSCOPE DU JOUR — mini bloc */}
      <section id="horoscope" className="night night-stars relative overflow-hidden py-24 scroll-mt-24" data-testid="home-horoscope">
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.2), transparent 60%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 text-center">
          <p className="font-mono text-xs tracking-[0.3em] gold-shimmer gold-shimmer-anim mb-4">ASTROLOGIE</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#f2f0ff] mb-3 leading-[1.08]">Les étoiles parlent,<br /><em className="gold-shimmer">écoutez votre signe.</em></h2>
          <p className="text-muted-night mb-10 max-w-xl mx-auto">Cliquez sur votre signe pour découvrir votre horoscope complet du jour.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {horoSigns.map((s) => (
              <button key={s.key} data-testid={`home-horo-${s.key}`} onClick={() => navigate(`/horoscope/${s.key}`)}
                className="group block rounded-3xl overflow-hidden relative aspect-[3/4] hover:-translate-y-1.5 transition-transform duration-300 shadow-md hover:shadow-xl text-left">
                <img src={ZODIAC_IMAGES[s.key]} alt={s.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(20,16,45,0.95) 8%, rgba(20,16,45,0.35) 48%, transparent 75%)" }} />
                <span className="absolute top-3 left-3 font-serif text-3xl text-goldsoft drop-shadow group-hover:scale-110 transition-transform">{s.symbol}</span>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-serif text-xl md:text-2xl text-[#f5f3ff] leading-tight">{s.name}</h3>
                  <p className="text-goldsoft/90 text-[10px] md:text-[11px] font-mono">{s.dates}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            <Link to="/horoscope" data-testid="home-horo-all" className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium"><Sparkles className="w-4 h-4" /> Voir tout l'horoscope</Link>
            <Link to="/astrologie" data-testid="home-astro-hub" className="btn-ghost-night inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium">Thème astral, numérologie & plus</Link>
          </div>
        </div>
      </section>

      {/* METHODE */}
      <section id="methode" className="bg-[var(--surface-2)] border-y border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-28">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] gold-shimmer mb-4">COMMENT ÇA MARCHE</p>
            <h2 className="font-serif text-4xl md:text-6xl max-w-2xl mb-20 leading-[1.06]">Quatre étapes<br /><em className="gold-shimmer">vers la clarté.</em></h2>
          </Reveal>
          <div>
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="grid md:grid-cols-[120px_1fr] gap-6 md:gap-16 py-10 border-t border-[var(--border)] group hover:bg-black/[0.02] transition-colors duration-500 px-2">
                  <span className="font-serif text-6xl md:text-7xl text-[var(--muted)]/40 group-hover:text-gold transition-colors duration-500">{c.n}</span>
                  <div className="max-w-2xl">
                    <h3 className="font-serif text-3xl md:text-4xl mb-3">{c.t}</h3>
                    <p className="text-[var(--muted)] leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FORFAITS */}
      <section id="forfaits" className="max-w-7xl mx-auto px-6 md:px-10 py-28 scroll-mt-24">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] gold-shimmer mb-4">FORFAITS · CRÉDIT EN EUROS</p>
          <h2 className="font-serif text-4xl md:text-6xl max-w-2xl mb-6 leading-[1.06]">Des forfaits clairs<br /><em className="gold-shimmer">sans surprise.</em></h2>
          <p className="text-[var(--muted)] max-w-xl mb-16">Vous achetez un crédit en euros, décompté à la minute selon le tarif du médium (à partir de {rate.toFixed(2).replace(".", ",")} €/min). Le minuteur s'arrête pile quand votre crédit est épuisé. Utilisable par téléphone ou chat, en plusieurs fois.</p>
        </Reveal>
        <div className="overflow-x-auto pt-5 pb-6 -mx-6 px-6 md:mx-0 md:px-0 snap-x snap-mandatory scroll-smooth no-scrollbar md:overflow-visible" data-testid="forfaits-carousel">
          <div className="flex items-stretch gap-6 w-max mx-auto md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 md:w-full md:max-w-5xl">
          {packages.map((p, i) => (
            <Reveal key={p.lookup_key} delay={i * 0.08} className="snap-center flex-none w-[290px] sm:w-[320px] md:w-auto">
              <div data-testid={`package-card-${p.lookup_key}`} className={`relative rounded-3xl p-8 h-full flex flex-col transition-all duration-300 hover:-translate-y-1.5 ${p.popular ? "bg-[#171334] text-[#f2f0ff] shadow-2xl ring-1 ring-gold/70 gold-glow" : "glass hover:shadow-xl"}`}>
                {p.popular && <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gold text-[#201a08] text-[11px] font-mono tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg">LE PLUS CHOISI</span>}
                <h3 className="font-serif text-2xl mb-1">{p.name}</h3>
                <p className={`text-xs mb-7 min-h-[32px] ${p.popular ? "text-muted-night" : "text-[var(--muted)]"}`}>{p.tagline}</p>
                <div className="mb-7">
                  <div className="flex items-end gap-2.5">
                    <span className={`font-serif text-5xl leading-none ${p.popular ? "text-goldsoft" : ""}`}>{p.price % 1 === 0 ? p.price : p.price.toFixed(2)}€</span>
                    <span className={`font-mono text-[10px] tracking-[0.18em] pb-1.5 ${p.popular ? "text-goldsoft/80" : "text-gold"}`}>DE CRÉDIT</span>
                  </div>
                  {p.bonus > 0 && <span className="inline-flex items-center mt-2.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full px-3 py-1">+ {p.bonus}€ offerts</span>}
                </div>
                <ul className={`space-y-2 mb-8 text-sm flex-1 ${p.popular ? "text-muted-night" : "text-[var(--muted)]"}`}>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> Téléphone & chat</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> Répartissable</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> Paiement 3D Secure</li>
                </ul>
                <button onClick={() => navigate(ctaTarget)} className={`w-full py-3 rounded-full text-sm font-medium ${p.popular ? "btn-gold" : "btn-ghost"}`}>Choisir ce forfait</button>
              </div>
            </Reveal>
          ))}
          </div>
        </div>
      </section>

      {/* AVIS / TESTIMONIALS — night */}
      <section id="avis" className="night night-stars relative overflow-hidden border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 relative z-10">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] text-goldsoft mb-4">AVIS CLIENTS</p>
            <h2 className="font-serif text-4xl md:text-6xl max-w-3xl mb-5 text-[#f2f0ff] leading-[1.06]">Des médiums reconnus<br /><em className="gold-shimmer">pour leur don.</em></h2>
            <p className="text-muted-night max-w-xl mb-3 leading-relaxed">Tous nos avis sont issus de véritables consultations. Découvrez les retours sincères de celles et ceux qui nous ont fait confiance.</p>
            <Link to="/avis-verification" data-testid="avis-verification-link" className="inline-flex items-center gap-1.5 text-goldsoft text-sm font-medium hover:underline mb-9">Comment nous vérifions les avis →</Link>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-5 mb-16">
              <div className="flex items-center gap-4">
                <span className="font-serif text-6xl text-goldsoft leading-none">{avgRating}</span>
                <div><Stars n={5} /><p className="text-muted-night text-xs mt-1.5">{reviewsTotal} avis clients</p></div>
              </div>
              <div className="grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap">
                {[["Disponible 7j/7", Clock], ["Confidentialité garantie", ShieldCheck], ["Bienveillance", Heart], ["Sans complaisance", Check]].map(([t, Icon]) => (
                  <span key={t} className="inline-flex items-center justify-center gap-2 text-xs border border-white/15 rounded-full px-4 py-2 text-[#e9e6ff]"><Icon className="w-3.5 h-3.5 text-goldsoft shrink-0" /> {t}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <div className="relative -mx-6 md:-mx-10 px-6 md:px-10">
            {/* Mobile & tablette : défilement automatique — une seule liste dans le DOM, boucle gérée en JS */}
            <div className="lg:hidden overflow-hidden">
              <div ref={reviewTrackRef} data-testid="reviews-track" className="flex items-start overflow-x-auto no-scrollbar">
                {reviews.map((r, i) => (
                  <div key={i} className="mx-3 shrink-0 w-[300px] sm:w-[360px]"><ReviewCard r={r} i={i} /></div>
                ))}
              </div>
            </div>
            {/* PC : grille complète, tous les avis lisibles d'un coup */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-6" data-testid="reviews-grid">
              {reviews.slice(0, 9).map((r, i) => (<ReviewCard key={i} r={r} i={i} />))}
            </div>
            {reviewsTotal > 9 && (
              <div className="hidden lg:flex justify-center mt-8">
                <span className="text-muted-night text-sm font-mono tracking-wider">+ {reviewsTotal - 9} autres avis</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {VALUES.map((v, i) => (
          <Reveal key={v.t} delay={i * 0.08}>
            <div>
              <v.icon className="w-7 h-7 text-gold mb-5" strokeWidth={1.3} />
              <h3 className="font-serif text-2xl mb-2">{v.t}</h3>
              <p className="text-[var(--muted)] text-sm leading-relaxed">{v.d}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* FAQ */}
      <section className="bg-[var(--surface-2)] border-y border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-28">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] text-gold mb-4">QUESTIONS FRÉQUENTES</p>
            <h2 className="font-serif text-4xl md:text-6xl mb-16 leading-[1.06]">Les réponses<br /><em className="gold-shimmer">à vos questions.</em></h2>
          </Reveal>
          <Accordion type="single" collapsible className="w-full">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-[var(--border)]">
                <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-serif text-xl md:text-2xl hover:text-gold hover:no-underline py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[var(--muted)] leading-relaxed text-base pb-6">{f.a.replace("{rate}", rate.toFixed(2).replace(".", ","))}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Texte éditorial SEO */}
      <SeoText title="Voyance Étoilée : la voyance en ligne, en toute confiance">
        <p>Voyance Étoilée est un site de <Link to="/voyance-en-ligne" className="text-gold hover:underline">voyance en ligne</Link> qui vous met en relation directe avec nos médiums. Trois façons de consulter, selon votre préférence : le <Link to="/voyance-par-tchat" className="text-gold hover:underline">tchat en direct</Link>, la réponse écrite détaillée <Link to="/voyance-par-email" className="text-gold hover:underline">par e-mail</Link> sous 8 heures, ou la <Link to="/voyance-par-telephone" className="text-gold hover:underline">consultation de voyance par téléphone</Link> — votre médium vous rappelle sur votre numéro, en France comme en Belgique ou en Suisse. Chaque échange est strictement confidentiel, et nos médiums sont disponibles 7j/7.</p>
        <p>La voyance sentimentale est au cœur de nos consultations : une rencontre, un doute dans votre couple, un retour affectif, une compatibilité à comprendre. Mais vos questions sur le travail, les finances, la famille ou un choix de vie important sont tout autant les bienvenues. Un médium ne vous dit pas ce que vous voulez entendre : il vous transmet ce qu'il perçoit, avec bienveillance et sans complaisance.</p>
        <p>Envie d'essayer ? La <Link to="/voyance-gratuite" className="text-gold hover:underline">voyance gratuite</Link> commence ici : 5 minutes offertes à l'inscription, sans carte bancaire, à utiliser par téléphone ou par tchat avec la médium de votre choix. Et en accès libre à toute heure : <Link to="/tirage-oracle" className="text-gold hover:underline">tirage gratuit</Link> de Tarot de Marseille et d'Oracle de Belline, <Link to="/horoscope" className="text-gold hover:underline">horoscope du jour</Link>, <Link to="/theme-astral" className="text-gold hover:underline">thème astral</Link>, <Link to="/numerologie" className="text-gold hover:underline">numérologie</Link> et <Link to="/compatibilite" className="text-gold hover:underline">compatibilité amoureuse</Link>.</p>
      </SeoText>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-28">
        <Reveal>
          <div className="night night-stars rounded-[2.5rem] p-12 md:p-20 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.35), transparent 60%)" }} />
            <div className="relative">
              <h2 className="font-serif text-4xl md:text-6xl mb-6 text-[#f2f0ff] leading-[1.06]">Ne restez pas<br /><em className="gold-shimmer">dans le doute.</em></h2>
              <p className="text-muted-night max-w-lg mx-auto mb-10">Faites le premier pas vers la clarté. Rejoignez Voyance Étoilée et consultez dès aujourd'hui.</p>
              <Link to={ctaTarget} data-testid="cta-bottom" className="btn-gold px-10 py-4 rounded-full text-sm font-medium inline-block">{user ? "Accéder à mon espace" : "Créer mon compte"}</Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />

      {/* Direct call dialog */}
      <Dialog open={!!callMedium} onOpenChange={(o) => !o && setCallMedium(null)}>
        <DialogContent className="glass border-[var(--border)] text-[var(--text)]" data-testid="home-call-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Être rappelé(e) par téléphone</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[var(--muted)]">Indiquez le numéro où vous souhaitez être rappelée. La consultation est décomptée de votre crédit à la minute.</p>
            <input data-testid="home-call-phone" type="tel" value={callPhone} onChange={(e) => setCallPhone(e.target.value)} placeholder="06 12 34 56 78"
              className="w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:border-[var(--violet)] outline-none transition-colors" />
            <button data-testid="home-call-confirm" disabled={callBusy || callPhone.trim().length < 6} onClick={submitCall} className="btn-gold w-full py-3 rounded-full text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"><Phone className="w-4 h-4" /> {callBusy ? "..." : "Être rappelé(e)"}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Direct email dialog */}
      <Dialog open={!!emailMedium} onOpenChange={(o) => !o && setEmailMedium(null)}>
        <DialogContent className="glass border-[var(--border)] text-[var(--text)]" data-testid="home-email-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Consultation par email</DialogTitle></DialogHeader>
          {emailMedium && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--muted)]">Écrivez votre question à <span className="text-[var(--text)] font-medium">{emailMedium.name}</span>. Réponse par email sous <strong>8 heures</strong>, sinon remboursement automatique.</p>
              <textarea data-testid="home-email-message" rows={5} value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} placeholder="Décrivez votre situation et vos questions..."
                className="w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 text-sm focus:border-gold outline-none transition-colors resize-none" />
              <button data-testid="home-email-pay" disabled={emailBusy} onClick={submitEmail} className="btn-gold w-full py-3 rounded-full text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"><Mail className="w-4 h-4" /> {emailBusy ? "..." : `Payer ${(emailMedium.email_rate_cents / 100).toFixed(2).replace(".", ",")} € et envoyer`}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

